import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { officeAPI } from './officesAPI';

// Fetch all offices
export const fetchOffices = createAsyncThunk(
  'offices/fetchOffices',
  async (_, { rejectWithValue }) => {
    try {
      const response = await officeAPI.fetchOffices();
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Fetch dealer offices
export const fetchDealerOffices = createAsyncThunk(
  'offices/fetchDealerOffices',
  async (dealerId, { rejectWithValue }) => {
    try {
      const response = await officeAPI.fetchDealerOffices(dealerId);
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Add new office
export const addOfficeAsync = createAsyncThunk(
  'offices/addOffice',
  async (officeData, { dispatch, rejectWithValue }) => {
    try {
      const response = await officeAPI.addOffice(officeData);
      console.log("Add office response:", response);
      dispatch(setSuccessMessage('Office added successfully!'));
      return response;
    } catch (error) {
      console.error("Add office error:", error);
      dispatch(setErrorMessage(error.message || 'Failed to add office'));
      return rejectWithValue(error.message);
    }
  }
);

// Edit office
export const editOfficeAsync = createAsyncThunk(
  'offices/editOffice',
  async ({ officeId, officeData }, { dispatch, rejectWithValue }) => {
    try {
      const response = await officeAPI.editOffice(officeId, officeData);
      console.log("Edit office response:", response);
      dispatch(setSuccessMessage('Office updated successfully!'));
      return response;
    } catch (error) {
      console.error("Edit office error:", error);
      dispatch(setErrorMessage(error.message || 'Failed to update office'));
      return rejectWithValue(error.message);
    }
  }
);

// Delete office
export const deleteOfficeAsync = createAsyncThunk(
  'offices/deleteOffice',
  async (officeId, { dispatch, rejectWithValue }) => {
    try {
      const response = await officeAPI.deleteOffice(officeId);
      console.log("Delete office response:", response);
      dispatch(setSuccessMessage('Office deleted successfully!'));
      return response;
    } catch (error) {
      console.error("Delete office error:", error);
      dispatch(setErrorMessage(error.message || 'Failed to delete office'));
      return rejectWithValue(error.message);
    }
  }
);

const officeSlice = createSlice({
  name: 'offices',
  initialState: {
    offices: [],
    loading: false,
    error: null,
    successMessage: null,
    refreshData: false,
  },
  reducers: {
    setSuccessMessage: (state, action) => {
      state.successMessage = action.payload;
    },
    setErrorMessage: (state, action) => {
      state.error = action.payload;
    },
    clearMessages: (state) => {
      state.successMessage = null;
      state.error = null;
    },
    setRefreshOfficesData: (state, action) => {
      state.refreshData = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch all offices
      .addCase(fetchOffices.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchOffices.fulfilled, (state, action) => {
        state.loading = false;
        state.offices = action.payload;
      })
      .addCase(fetchOffices.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to fetch offices';
      })
      // Fetch dealer offices
      .addCase(fetchDealerOffices.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDealerOffices.fulfilled, (state, action) => {
        state.loading = false;
        state.offices = action.payload;
      })
      .addCase(fetchDealerOffices.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to fetch dealer offices';
      })
      // Add office
      .addCase(addOfficeAsync.pending, (state) => {
        state.loading = true;
      })
      .addCase(addOfficeAsync.fulfilled, (state) => {
        state.loading = false;
        state.refreshData = true;
      })
      .addCase(addOfficeAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Edit office
      .addCase(editOfficeAsync.pending, (state) => {
        state.loading = true;
      })
      .addCase(editOfficeAsync.fulfilled, (state) => {
        state.loading = false;
        state.refreshData = true;
      })
      .addCase(editOfficeAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Delete office
      .addCase(deleteOfficeAsync.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteOfficeAsync.fulfilled, (state) => {
        state.loading = false;
        state.refreshData = true;
      })
      .addCase(deleteOfficeAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { 
  setSuccessMessage, 
  setErrorMessage, 
  clearMessages,
  setRefreshOfficesData 
} = officeSlice.actions;

export const selectOffices = (state) => state.offices.offices;
export const selectOfficesLoading = (state) => state.offices.loading;
export const selectOfficesError = (state) => state.offices.error;
export const selectSuccessMessage = (state) => state.offices.successMessage;
export const selectRefreshOfficesData = (state) => state.offices.refreshData;

export default officeSlice.reducer;